/**
 * Hook: Fetch XMR price in a fiat currency from Kraken's public API.
 * Polls every 5 minutes.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getXmrPrice } from '@/lib/monero/fiat';

export function useFiatPrice(fiatCode: string) {
  const [price, setPrice] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const fetchPrice = useCallback(async () => {
    try {
      const p = await getXmrPrice(fiatCode);
      if (p !== null && p > 0) {
        setPrice(p);
        setError(null);
      } else {
        setError('Failed to fetch price');
      }
    } catch {
      setError('Failed to fetch price');
    } finally {
      setLoading(false);
    }
  }, [fiatCode]);

  useEffect(() => {
    setLoading(true);
    setPrice(null);
    setError(null);
    fetchPrice();

    // Poll every 5 minutes
    intervalRef.current = setInterval(fetchPrice, 5 * 60 * 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchPrice]);

  return { price, loading, error, refresh: fetchPrice };
}
